import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { listReturnUrl } from '../lib/listReturn';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { normalizeDate } from '../lib/dates';
import { useClients, type LookupOption } from '../lib/lookups';
import { useAuth, can } from '../lib/auth';
import { ROLE } from '../lib/permissions';

/** Order types, with the hint shown beside each radio. */
const ORDER_TYPES = [
  { value: 'Unconfirmed', hint: '(exists, but Repeat or Change not confirmed)' },
  { value: 'New', hint: '(brand-new product)' },
  { value: 'Proofing', hint: '(sample/proof run)' },
  { value: 'Change', hint: '(spec change)' },
  { value: 'Repeat', hint: '(same as before)' },
] as const;

/** '' = an existing product picked, and the user has not said which type it is yet. */
type OrderType = (typeof ORDER_TYPES)[number]['value'] | '';

/** Today, as the date box takes it (yyyy-mm-dd, local). */
const todayIso = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };

interface POForm {
  poNumber: string; poDate: string; poDeliveryDate: string;
  orderType: OrderType;
  jobName: string; client: string; artwork: string;
  quantity: string; pricePerBox: string;
  amount: string; vat: string; finalAmount: string;
  materialNo: string; description: string;
  /** The product's A/W Material No — typed here, saved to the product. */
  awMaterialNo: string;
  productId: string;
  crmUserId: string;
  dispatchAddress: string;
}

const empty: POForm = {
  poNumber: '', poDate: '', poDeliveryDate: '',
  // No product linked yet, so by the rule this is a New product. Picking one
  // from the master flips it to Repeat/Change.
  orderType: 'New',
  jobName: '', client: '', artwork: '',
  quantity: '0', pricePerBox: '0',
  amount: '0', vat: '0', finalAmount: '0',
  materialNo: '', description: '',
  awMaterialNo: '',
  productId: '',
  crmUserId: '',
  dispatchAddress: '',
};

interface ProductSuggest {
  id: string; name: string; client: string | null; dieNumber: string | null;
}
interface ProductFull {
  id: string; name: string; client: string; artwork: string; dieNumber: string;
  productRate: string;
  artWNo?: string;
  lastOrderType?: string | null;
}

/** An earlier PO carrying the same customer item code. */
interface ItemCodeMatch {
  poId: string; poNumber: string; poDate: string;
  artwork: string; productId: string; jobName: string; client: string; orderType: string;
}

export function PurchaseOrderFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  // CRM and Purchase read POs but do not edit them; Admin reads everything and
  // edits nothing. Asked as a capability so the answer changes in one place.
  const { user } = useAuth();
  const canEditPO = can(user, isEdit ? 'EDIT_PO' : 'CREATE_PO');
  const navigate = useNavigate();
  const qc = useQueryClient();
  // A new PO starts on today's date; it can be changed.
  const [form, setForm] = useState<POForm>(() => (isEdit ? empty : { ...empty, poDate: todayIso() }));
  const [error, setError] = useState<string | null>(null);
  const [dupWarning, setDupWarning] = useState<{ id: string; jobName: string; productId: string }[]>([]);

  useEffect(() => {
    if (isEdit) return;
    const poNum = form.poNumber.trim();
    const poDate = form.poDate.trim();
    if (!poNum || !poDate) { setDupWarning([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api<{ matches: { id: string; jobName: string; productId: string }[] }>(
          `/api/purchase-orders/check-duplicate?poNumber=${encodeURIComponent(poNum)}&poDate=${encodeURIComponent(poDate)}`,
        );
        setDupWarning(r.matches);
      } catch { setDupWarning([]); }
    }, 400);
    return () => clearTimeout(t);
  }, [form.poNumber, form.poDate, isEdit]);

  // Product autocomplete (the authoritative product link — drives the Job
  // Card's specs so the wrong-product mismatch can't happen again).
  const [originalArtwork, setOriginalArtwork] = useState('');
  const [lastOrderWasProofing, setLastOrderWasProofing] = useState(false);


  // Product autocomplete (the authoritative product link — drives the Job
  // Card's specs so the wrong-product mismatch can't happen again).
  const [productInput, setProductInput] = useState('');
  const [productSuggestions, setProductSuggestions] = useState<ProductSuggest[]>([]);
  const [showProductSuggest, setShowProductSuggest] = useState(false);

  const clients = useClients();

  const existing = useQuery({
    enabled: isEdit,
    queryKey: ['po', id],
    queryFn: () => api<POForm & { id: string; isCancelled?: boolean; cancelReason?: string; jobCards?: number; jobCardedQty?: number }>(`/api/purchase-orders/${id}`),
  });

  useEffect(() => {
    if (existing.data) {
      setForm({ ...empty, ...existing.data });
      if (existing.data.productId) {
        api<ProductFull>(`/api/products/${encodeURIComponent(existing.data.productId)}`)
          .then((p) => {
            setProductInput(p.name);
            setOriginalArtwork(p.artwork || '');
          })
          .catch(() => {});
      } else {
        setProductInput(existing.data.jobName);
      }
    }
  }, [existing.data]);

  // When editing an existing PO with a linked product, show its name in the
  // product field.
  useEffect(() => {
    if (!form.productId || productInput) return;
    api<ProductFull>(`/api/products/${encodeURIComponent(form.productId)}`)
      .then((p) => setProductInput(p.name))
      .catch(() => { /* product may have been deleted */ });
  }, [form.productId, productInput]);

  // Debounced product search.
  useEffect(() => {
    if (!productInput || productInput.length < 2) { setProductSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api<ProductSuggest[]>(
          `/api/products/autocomplete?q=${encodeURIComponent(productInput)}`,
        );
        setProductSuggestions(r);
      } catch { setProductSuggestions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [productInput]);

  // Recompute amount on qty/price change; final on amount+vat
  useEffect(() => {
    const qty = Number(form.quantity) || 0;
    const price = Number(form.pricePerBox) || 0;
    const gross = qty * price;
    const vat = Number(form.vat) || 0;
    const final = gross + (gross * vat) / 100;
    setForm((f) => ({
      ...f,
      amount: gross.toFixed(2),
      finalAmount: final.toFixed(2),
    }));
  }, [form.quantity, form.pricePerBox, form.vat]);

  // CRM owner is assigned per PO at entry. The dropdown pre-selects whoever
  // handled this client's last PO, so the common case is zero extra clicks and
  // no client->CRM mapping table is needed.
  const crms = useQuery({
    queryKey: ['flow-crms'],
    queryFn: () => api<{ id: number; name: string }[]>('/api/flow/crms'),
    staleTime: 300_000,
  });

  useEffect(() => {
    if (isEdit || !form.client || form.crmUserId) return;
    let cancelled = false;
    api<{ id: number; name: string } | null>(
      `/api/flow/suggest-crm/${encodeURIComponent(form.client)}`,
    ).then((s) => {
      if (!cancelled && s) setForm((f) => (f.crmUserId ? f : { ...f, crmUserId: String(s.id) }));
    }).catch(() => { /* no suggestion is fine — the field stays empty */ });
    return () => { cancelled = true; };
  }, [form.client, form.crmUserId, isEdit]);

  useEffect(() => {
    if (!form.client) { setForm((f) => ({ ...f, dispatchAddress: '' })); return; }
    let cancelled = false;
    api<{ dispatchAddress?: string }>(`/api/clients/${encodeURIComponent(form.client)}`)
      .then((c) => {
        if (!cancelled) setForm((f) => ({ ...f, dispatchAddress: c.dispatchAddress || '' }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [form.client]);

  // The API rejects an identical PO line with "duplicate_po:<existing PO_ID>".
  // Surface which PO it clashed with, so the user can go look at it rather
  // than just being told "no".
  function describeSaveError(e: Error): string {
    if (e.message === 'client_required') return 'Client is required.';
    if (e.message === 'invalid_client') return 'Selected client does not exist. Please pick a valid client.';
    if (e.message === 'item_code_required') {
      return "Item Code is required. Enter the customer's code, or NA if they did not give one.";
    }
    const mp = /^duplicate_po_product:(.+)$/.exec(e.message);
    if (mp) return `A PO with this number, date, and product already exists (${mp[1]}). Cannot create a duplicate.`;
    const m = /^duplicate_po:(.+)$/.exec(e.message);
    if (!m) return e.message;
    return `This purchase order already exists as ${m[1]} — same PO number, quantity, date and product. `
         + `Open that PO instead, or change one of those fields if this is genuinely a separate order.`;
  }

  const save = useMutation({
    mutationFn: () =>
      api<{ id: string }>(isEdit ? `/api/purchase-orders/${id}` : '/api/purchase-orders', {
        method: isEdit ? 'PUT' : 'POST',
        body: JSON.stringify({ ...form, jobName: productInput }),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pos'] }); navigate(listReturnUrl('/purchase-orders')); },
    onError: (e) => setError(describeSaveError(e as Error)),
  });

  function update<K extends keyof POForm>(k: K, v: POForm[K]) {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === 'orderType' && v === 'Change') {
        next.artwork = '';
      }
      return next;
    });

  }



  /** The linked product's client in Product Master (a Client_ID), '' when none. */
  const [productClient, setProductClient] = useState('');
  /** The client the user chose to keep although it is not the product's. */
  const [keptClient, setKeptClient] = useState('');

  async function selectProduct(p: ProductSuggest) {
    setProductInput(p.name);
    setShowProductSuggest(false);
    try {
      const full = await api<ProductFull>(`/api/products/${encodeURIComponent(p.id)}`);
      const originalAW = full.artwork || '';
      setOriginalArtwork(originalAW);
      const proofing = full.lastOrderType === 'Proofing';
      setLastOrderWasProofing(proofing);
      const pc = (full.client ?? '').trim();
      setProductClient(pc);
      setKeptClient('');
      setForm((f) => {
        const nextAW = full.artwork || f.artwork;
        return {
          ...f,
          productId: full.id,
          jobName: full.name || f.jobName,
          artwork: nextAW,
          // The product's A/W Material No, when it has one.
          awMaterialNo: full.artWNo?.trim() ? full.artWNo : f.awMaterialNo,
          pricePerBox: full.productRate || f.pricePerBox,
          // The client comes with the product; one already picked that differs
          // is asked about below rather than overwritten.
          client: f.client || pc,
          // An existing product is a Repeat or a Change - the user says which.
          // Nothing is picked for them (a product last run as a proof is New).
          // Nothing is chosen for them: the form will not save until they pick.
          orderType: proofing ? 'New' : (f.orderType === 'Repeat' || f.orderType === 'Change' || f.orderType === 'Unconfirmed' ? f.orderType : ''),
        };
      });
    } catch {
      setForm((f) => ({ ...f, productId: p.id }));
    }
  }

  function clearProduct() {
    setProductClient('');
    setKeptClient('');
    setForm((f) => ({ ...f, productId: '', orderType: 'New' }));
    setProductInput('');
    setOriginalArtwork('');
    setLastOrderWasProofing(false);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!productInput.trim()) { setError('Product / Job Name is required.'); return; }
    if (!form.orderType) { setError('Choose the order type - Repeat, Change, or Unconfirmed if the client has not said.'); return; }
    if (!form.client.trim()) { setError('Client is required.'); return; }
    if (clientMismatch) { setError('This product belongs to another client in Product Master - choose which client this PO is for.'); return; }
    if (!form.materialNo.trim()) {
      setError("Item Code is required. Enter the customer's code, or NA if they did not give one.");
      return;
    }
    const exactDup = form.productId ? dupWarning.filter((d) => d.productId === form.productId) : [];
    if (exactDup.length > 0) {
      setError(`A PO with this number, date, and product already exists (${exactDup.map((d) => d.id).join(', ')}). Cannot create a duplicate.`);
      return;
    }
    save.mutate();
  }

  if (isEdit && existing.isLoading) return <div className="p-6 text-slate-500">Loading…</div>;

  const selectedClient = clients.data?.find((o) => o.id === form.client);
  const clientName = (cid: string) => clients.data?.find((o) => o.id === cid)?.name ?? cid;
  // The PO's client is not the one the product carries, and nobody has said it should be.
  const clientMismatch = !isEdit && !!form.productId && !!productClient && !!form.client
    && form.client !== productClient && keptClient !== form.client;
  // The artwork differs from the product's: a hint towards Change, never a choice made.
  const artworkDiffers = !!form.productId && !!originalArtwork && !!form.artwork && form.artwork !== originalArtwork;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-brand">
          {!canEditPO ? `Purchase Order — ${id}` : isEdit ? `Edit PO — ${id}` : 'New Purchase Order'}
        </h1>
        <div className="flex items-center gap-4">
          {isEdit && id && (
            <Link to={`/purchase-orders/${encodeURIComponent(id)}/print`} className="text-sm text-brand hover:underline">Print / PDF</Link>
          )}
          <Link to={listReturnUrl('/purchase-orders')} className="text-sm text-slate-600 hover:underline">← Back</Link>
        </div>
      </div>

      {!canEditPO && (
        <div className="mb-4 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
          View only — purchase orders are maintained by CRM entry.
        </div>
      )}

      <form className="space-y-4" onSubmit={onSubmit}>
        {/* One `fieldset disabled` rather than a disabled prop per input: a
            field added later cannot accidentally stay editable for a read-only
            role. The API refuses the write either way. */}
        <fieldset disabled={!canEditPO} className="space-y-4 disabled:opacity-100">
        <Section title="Product">
          <div className="md:col-span-3 relative">
            <label className="label">
              Product / Job Name *
              {form.productId && (
                <span className="ml-2 text-xs font-normal text-emerald-700">
                  linked → {form.productId}
                </span>
              )}
            </label>
            <div className="flex gap-2">
              <input
                className="input flex-1"
                value={productInput}
                onChange={(e) => {
                  setProductInput(e.target.value);
                  setShowProductSuggest(true);
                  // Typing breaks the link — user must re-select to relink.
                  // Reset the type too, otherwise the form is left on a
                  // Repeat/Change that is now disabled and unselectable.
                  if (form.productId) { setForm((f) => ({ ...f, productId: '', orderType: 'New' })); setProductClient(''); setKeptClient(''); }
                }}
                onFocus={() => setShowProductSuggest(true)}
                onBlur={() => setTimeout(() => setShowProductSuggest(false), 150)}
                placeholder="Type at least 2 characters to search Product Master…"
              />
              {form.productId && (
                <button type="button" className="btn-ghost" onClick={clearProduct}>
                  Clear
                </button>
              )}
            </div>
            {showProductSuggest && productSuggestions.length > 0 && (
              <div className="absolute z-10 mt-1 w-full max-h-60 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
                {productSuggestions.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100"
                    onMouseDown={(e) => { e.preventDefault(); selectProduct(s); }}
                  >
                    <div className="font-medium">{s.name}</div>
                    <div className="text-[11px] text-slate-500">
                      {s.id}{s.dieNumber ? ` · Die ${s.dieNumber}` : ''}{s.client ? ` · ${s.client}` : ''}
                    </div>
                  </button>
                ))}
              </div>
            )}
            <p className="mt-1 text-xs text-slate-500">
              Picking a product here is what the Job Card will use for sheet,
              paper, die and ups. Leaving it blank falls back to the legacy
              artwork/client guess and can pick the wrong product.
            </p>
          </div>
        </Section>

        <Section title="Order">
          <F label="PO Number" value={form.poNumber} onChange={(v) => update('poNumber', v)} />
          <F label="PO Date" type="date" value={form.poDate} onChange={(v) => update('poDate', v)} />
          <div className="md:col-span-3">
            <label className="label">
              Order Type
              <span className="ml-2 text-xs font-normal text-slate-500">
                {form.productId
                  ? 'product found in master — choose Repeat or Change'
                  : 'not in Product Master — this is a New product, and one will be created on save'}
              </span>
            </label>
            {form.productId && !form.orderType && (
              <p className="mb-1.5 text-xs text-amber-700">
                Choose one: same as before (Repeat), the spec has changed (Change), or Unconfirmed if the client has not said yet.
              </p>
            )}
            {artworkDiffers && form.orderType !== 'Change' && (
              <p className="mb-1.5 text-xs text-amber-700">
                The artwork / design no. differs from the product's ({originalArtwork}) — this may be a Change.
              </p>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
              {ORDER_TYPES.map(({ value, hint }) => {
                // New means "product does not exist yet", so it only applies
                // when nothing is linked; everything else needs a product.
                // Existence in Product Master decides it: no product means a
                // brand-new one (the stub is created on save), a linked product
                // means Repeat or Change — Unconfirmed only until the client
                // says which of the two.
                const isSuperAdmin = user?.rollId === ROLE.SuperAdmin;
                const isNewLike = value === 'New' || value === 'Proofing';
                const disabled = isEdit && isSuperAdmin
                  ? false
                  : isNewLike
                    ? (!!form.productId && !(value === 'New' && lastOrderWasProofing))
                    : !form.productId;
                return (
                  <label key={value}
                         className={`flex items-center gap-2 ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                         title={disabled
                           ? (isNewLike
                               ? 'A product is linked, so this is not a new product'
                               : 'Search and pick the product below first, or leave as Unconfirmed')
                           : ''}>
                    <input type="radio" disabled={disabled}
                           checked={form.orderType === value}
                           onChange={() => update('orderType', value)} />
                    <span>{value}</span>
                    <span className="text-xs text-slate-500">{hint}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </Section>

        <Section title="Specs & Client">
          <Sel label="Client" value={form.client} onChange={(v) => update('client', v)} options={clients.data ?? []} />
          {clientMismatch && (
            <div className="md:col-span-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <div className="font-medium">This product is <b>{clientName(productClient)}</b>'s in Product Master, but the PO has <b>{clientName(form.client)}</b>.</div>
              <div className="mt-1.5 flex flex-wrap gap-2">
                <button type="button" className="btn-primary text-xs py-1" onClick={() => { update('client', productClient); setKeptClient(''); }}>
                  Use {clientName(productClient)} (the product's)
                </button>
                <button type="button" className="btn-ghost text-xs py-1 border border-amber-300" onClick={() => setKeptClient(form.client)}>
                  Keep {clientName(form.client)}
                </button>
              </div>
            </div>
          )}
          {selectedClient && (
            <div className="md:col-span-3 text-xs text-slate-500">
              Selected client: <span className="font-medium text-slate-700">{selectedClient.name}</span>
            </div>
          )}
          {form.client && (
            <div className="md:col-span-3">
              <label className="label">Dispatch Address</label>
              <textarea
                className="input min-h-[60px]"
                value={form.dispatchAddress}
                onChange={(e) => update('dispatchAddress', e.target.value)}
                placeholder="Auto-filled from client master"
              />
              <p className="mt-0.5 text-[11px] text-slate-500">
                From client master. Changes here update the client record on save.
              </p>
            </div>
          )}
          {/* Hidden only for a Change with no artwork yet — that is the case
              where the new design number does not exist until Design creates
              it, and an empty box invites a guess.
              It stays VISIBLE when there is a value, because a Change detected
              from the item code was detected precisely BY the artwork the
              operator just typed. Hiding it there pulls their own evidence off
              the screen a second after they entered it. */}
          {(form.orderType !== 'Change' || form.artwork) && (
            <F label="Artwork / Design No" value={form.artwork} onChange={(v) => update('artwork', v)} />
          )}
          <F label="Item Code" value={form.materialNo} onChange={(v) => update('materialNo', v)}
             required
             hint="The customer's own code. Always ask; type NA only if they do not issue one." />
          <F label="A/W Material No" value={form.awMaterialNo} onChange={(v) => update('awMaterialNo', v)}
             hint="From the customer's PO. Saved to the product — the Job Card and Design Queue show it." />
        </Section>

        <Section title="Amount">
          <F label="Quantity (boxes)" type="number" value={form.quantity} onChange={(v) => update('quantity', v)} />
          <F label="Price per Box" type="number" value={form.pricePerBox} onChange={(v) => update('pricePerBox', v)} />
          <F label="GST %" type="number" value={form.vat} onChange={(v) => update('vat', v)} />
          <Read label="Gross Amount" value={form.amount} />
          <Read label="Final Amount (incl GST)" value={form.finalAmount} />
        </Section>

        <div className="card">
          <div className="card-header">PO Description / Remark</div>
          <div className="card-body space-y-3">
            <PastRemarks productId={form.productId} exclude={id} />
            <textarea className="input min-h-[100px]" value={form.description}
              placeholder="Anything to remember about this order - it shows on the next PO of this product."
              onChange={(e) => update('description', e.target.value)} />
          </div>
        </div>
        </fieldset>

        {dupWarning.length > 0 && !isEdit && form.productId && dupWarning.some((d) => d.productId === form.productId) && (
          <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
            <strong>Blocked:</strong> A PO with this number, date, and product already exists:{' '}
            {dupWarning.filter((d) => d.productId === form.productId).map((d, i) => (
              <span key={d.id}>{i > 0 && ', '}<Link to={`/purchase-orders/${d.id}`} className="underline font-medium">{d.id}</Link>{d.jobName ? ` (${d.jobName})` : ''}</span>
            ))}
          </div>
        )}
        {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

        <div className="flex justify-end gap-2">
          <Link to={listReturnUrl('/purchase-orders')} className="btn-ghost">
            {canEditPO ? 'Cancel' : 'Back'}
          </Link>
          {canEditPO && (
            <button type="submit" className="btn-primary" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : isEdit ? 'Update' : 'Create'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="card-header">{title}</div>
      <div className="card-body grid gap-4 md:grid-cols-3">{children}</div>
    </div>
  );
}
function F({ label, value, onChange, onBlur, type = 'text', step, required, hint, note }:
  { label: string; value: string; onChange: (v: string) => void; onBlur?: (v: string) => void;
    type?: string; step?: string;
    required?: boolean; hint?: string;
    /** Live feedback shown under the hint — e.g. the item-code match result. */
    note?: { text: string; tone: 'info' | 'good' | 'warn' } | null }) {
  const displayValue = type === 'date' ? normalizeDate(value) : value;
  const empty = required && !value.trim();
  const toneClass = note?.tone === 'good' ? 'text-emerald-700'
    : note?.tone === 'warn' ? 'text-amber-700'
    : 'text-slate-600';
  return (
    <div>
      <label className="label">
        {label}
        {required && <span className="ml-1 text-red-600" title="Required">*</span>}
      </label>
      <input
        className={`input ${empty ? 'border-red-300 bg-red-50' : ''}`}
        type={type}
        value={displayValue}
        onChange={(e) => onChange(e.target.value)}
        onBlur={(e) => onBlur?.(e.target.value)}
        step={step ?? (type === 'number' ? 'any' : undefined)}
      />
      {hint && <p className="mt-0.5 text-[11px] text-slate-500">{hint}</p>}
      {note && <p className={`mt-0.5 text-[11px] font-medium ${toneClass}`}>{note.text}</p>}
    </div>
  );
}
function Read({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input bg-slate-50" value={value} readOnly />
    </div>
  );
}
function Sel({ label, value, onChange, options }:
  { label: string; value: string; onChange: (v: string) => void; options: LookupOption[] }) {
  return (
    <div>
      <label className="label">{label}</label>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        {value && !options.find((o) => o.id === value) && <option value={value}>{value} (legacy)</option>}
      </select>
    </div>
  );
}

/** What was written on this product's earlier POs, newest first. */
function PastRemarks({ productId, exclude }: { productId: string; exclude?: string }) {
  const q = useQuery({
    enabled: !!productId,
    queryKey: ['po-product-remarks', productId, exclude ?? ''],
    queryFn: () => api<{ rows: { poId: string; poNumber: string; poDate: string; enteredOn: string; remark: string }[] }>(
      `/api/purchase-orders/product-remarks?productId=${encodeURIComponent(productId)}&exclude=${encodeURIComponent(exclude ?? '')}`,
    ),
    staleTime: 60_000,
  });
  if (!productId) return null;
  const rows = q.data?.rows ?? [];
  if (q.isLoading) return <div className="text-xs text-slate-400">Loading earlier remarks…</div>;
  if (!rows.length) return <div className="text-xs text-slate-400">No remarks on earlier POs of this product.</div>;
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
      <div className="mb-1 text-xs font-semibold uppercase text-amber-800">Remarks on earlier POs of this product</div>
      <ul className="max-h-48 space-y-1.5 overflow-y-auto text-sm">
        {rows.map((r) => (
          <li key={r.poId} className="border-b border-amber-100 pb-1.5 last:border-0 last:pb-0">
            <div className="text-[11px] text-amber-700">
              PO {r.poNumber || r.poId}{r.poDate ? ` · ${r.poDate}` : r.enteredOn ? ` · ${r.enteredOn}` : ''}
            </div>
            <div className="whitespace-pre-wrap text-slate-800">{r.remark}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
